import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as providerPriceListItemOptionValueActions from '@redux/modules/providerPriceListItemOptionValue'

import defaultFormState, { requiredFields } from '@models/providerPriceListItemOptionValue'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type {
  ProviderPriceListItemOptionValueModel,
  ProviderPriceListItemOptionValueRequestOptions,
} from '@models/types'

export const generateUrls = (providerPriceListItemOptionValue?: Partial<ProviderPriceListItemOptionValueModel>) => {
  const { id } = providerPriceListItemOptionValue || {}

  return {
    editProviderPriceListItemOptionValueDetailsUrl:
      `#/systemManager/providerPriceListItemOptionValues/${id}/edit/details`,
    editProviderPriceListItemOptionValueUrl: `#/systemManager/providerPriceListItemOptionValues/${id}/edit/`,
    providerPriceListItemOptionValuesIndexUrl: '#/systemManager/providerPriceListItemOptionValues',
  }
}

type CreateProviderPriceListItemOptionValueParams = {
  dispatch: AppDispatch,
  providerPriceListItemOptionValueParams: Partial<ProviderPriceListItemOptionValueModel>,
  requestOptions?: ProviderPriceListItemOptionValueRequestOptions,
}

const createProviderPriceListItemOptionValue = (params: CreateProviderPriceListItemOptionValueParams) => {
  const { dispatch, providerPriceListItemOptionValueParams, requestOptions } = params
  const { createProviderPriceListItemOptionValue: createFn } = providerPriceListItemOptionValueActions

  return dispatch(createFn(providerPriceListItemOptionValueParams, requestOptions))
}

type UpdateProviderPriceListItemOptionValueParams = {
  dispatch: AppDispatch,
  providerPriceListItemOptionValue: ProviderPriceListItemOptionValueModel,
  providerPriceListItemOptionValueParams: Partial<ProviderPriceListItemOptionValueModel>,
  requestOptions?: ProviderPriceListItemOptionValueRequestOptions,
}

const updateProviderPriceListItemOptionValue = (params: UpdateProviderPriceListItemOptionValueParams) => {
  const {
    dispatch, providerPriceListItemOptionValue, providerPriceListItemOptionValueParams, requestOptions,
  } = params
  const { updateProviderPriceListItemOptionValue: updateFn } = providerPriceListItemOptionValueActions

  const updatedParams = {
    id: providerPriceListItemOptionValue.id,
    ...providerPriceListItemOptionValueParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type UpdateDefaultProviderPriceListItemOptionValueParams = {
  dispatch: AppDispatch,
  providerPriceListItemOptionValue: ProviderPriceListItemOptionValueModel,
  requestOptions?: ProviderPriceListItemOptionValueRequestOptions,
}

const updateDefaultProviderPriceListItemOptionValue = (params: UpdateDefaultProviderPriceListItemOptionValueParams) => {
  const {
    dispatch, providerPriceListItemOptionValue, requestOptions,
  } = params
  const { updateDefaultProviderPriceListItemOptionValue: updateFn } = providerPriceListItemOptionValueActions

  return dispatch(updateFn(providerPriceListItemOptionValue, requestOptions))
}

type DeleteProviderPriceListItemOptionValueParams = {
  dispatch: AppDispatch,
  providerPriceListItemOptionValue: DeleteParams<ProviderPriceListItemOptionValueModel>,
}

const deleteProviderPriceListItemOptionValue = (params: DeleteProviderPriceListItemOptionValueParams) => {
  const { dispatch, providerPriceListItemOptionValue } = params
  const { deleteProviderPriceListItemOptionValue: deleteFn } = providerPriceListItemOptionValueActions

  return dispatch(deleteFn(providerPriceListItemOptionValue))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useProviderPriceListItemOptionValueForm(
  providerPriceListItemOptionValue: Partial<ProviderPriceListItemOptionValueModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const providerPriceListItemOptionValueForm = useForm(
    defaultFormState,
    {
      entity: providerPriceListItemOptionValue,
      requiredFields: [...requiredFields, ...customRequiredFields],
      validateOn,
    },
    [providerPriceListItemOptionValue.id, providerPriceListItemOptionValue.cache_key],
  )

  return {
    ...providerPriceListItemOptionValueForm,
  }
}

export const useRelations = (providerPriceListItemOptionValue: Partial<ProviderPriceListItemOptionValueModel> = {}) => {
  const { provider_price_list_item_option_id } = providerPriceListItemOptionValue

  const { providerPriceListItemOptions } = useSelector(reduxState => reduxState.entities)

  const providerPriceListItemOption = provider_price_list_item_option_id
    && providerPriceListItemOptions[provider_price_list_item_option_id]
    ? providerPriceListItemOptions[provider_price_list_item_option_id]
    : {}

  return {
    providerPriceListItemOption,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: ProviderPriceListItemOptionValueRequestOptions,
}

function useProviderPriceListItemOptionValue(
  initEntity: Partial<ProviderPriceListItemOptionValueModel> = {},
  options: Options = {},
) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: providerPriceListItemOptionValue }: { entity: ProviderPriceListItemOptionValueModel} = useLatestEntity(initEntity, 'providerPriceListItemOptionValues')
  const { id } = providerPriceListItemOptionValue

  const dispatch = useDispatch()

  const { providerPriceListItemOption } = useRelations(providerPriceListItemOptionValue)

  useReduxAction(
    'providerPriceListItemOptionValues',
    'loadProviderPriceListItemOptionValue',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.providerPriceListItemOptionValues)

  return {
    callbacks: {
      createProviderPriceListItemOptionValue: (
        providerPriceListItemOptionValueParams: Partial<ProviderPriceListItemOptionValueModel>,
        entityOptions?: ProviderPriceListItemOptionValueRequestOptions,
      ) => (
        createProviderPriceListItemOptionValue({
          dispatch,
          providerPriceListItemOptionValueParams,
          requestOptions: entityOptions,
        })
      ),
      deleteProviderPriceListItemOptionValue: () => (
        deleteProviderPriceListItemOptionValue({ dispatch, providerPriceListItemOptionValue })
      ),
      toggleVisibility: (entityOptions?: ProviderPriceListItemOptionValueRequestOptions) => (
        updateProviderPriceListItemOptionValue({
          dispatch,
          providerPriceListItemOptionValue,
          providerPriceListItemOptionValueParams: { hidden: !providerPriceListItemOptionValue.hidden },
          requestOptions: entityOptions,
        })
      ),
      toggleDefault: (entityOptions?: ProviderPriceListItemOptionValueRequestOptions) => (
        updateDefaultProviderPriceListItemOptionValue({
          dispatch,
          providerPriceListItemOptionValue,
          requestOptions: entityOptions,
        })
      ),
      updateProviderPriceListItemOptionValue: (
        providerPriceListItemOptionValueParams: Partial<ProviderPriceListItemOptionValueModel>,
        entityOptions?: ProviderPriceListItemOptionValueRequestOptions,
      ) => (
        updateProviderPriceListItemOptionValue({
          dispatch,
          providerPriceListItemOptionValue,
          providerPriceListItemOptionValueParams,
          requestOptions: entityOptions,
        })
      ),
    },
    creating,
    deleting,
    loading: loading && !loadedIds.includes(providerPriceListItemOptionValue.id),
    providerPriceListItemOptionValue,
    providerPriceListItemOption,
    updating,
    urls: generateUrls(providerPriceListItemOptionValue),
  }
}

export type UseProviderPriceListItemOptionValuePayload = ReturnType<typeof useProviderPriceListItemOptionValue>

export default useProviderPriceListItemOptionValue
